import React, { FC, useEffect, useState } from "react";
import * as signalR from "@microsoft/signalr";
import {
  Button,
  Modal,
  Select,
  Form,
  DatePicker,
  Table,
  Popconfirm,
  Switch
} from "antd";
import moment from "moment";

import useHttpGet from "../../hooks/useHttpGet";
import { IResultListResponse } from "../../models/response/result-list.model";
import { IInstrument } from "../../models/instrument.model";
import { APIRoutes } from "../../http";
import CommonService from "../../services/common.service";
import { formItemLayout } from "../../services/constants";
import { CITypes, EngineModalMode, LogLevelsTypes } from "../../services/enums";

const { RangePicker } = DatePicker;

const Engine: FC = () => {
  const [hubConnection, setHubConnection] = useState<any>(null);
  const [modalMode, setModalMode] = useState<EngineModalMode>();
  const [visible, setIsModalVisible] = useState<boolean>();
  const [logs, setLogs] = useState<any[]>([]);
  const [instrumentIdsWithQuotesOn, setInstrumentIdsWithQuotesOn] = useState<any[]>([]);
  const [instrumentsFilter, setInstrumentsFilter] = useState<any>({
    sortPropertyName: "sortNumber",
    sortAscending: true,
    pageSize: 50,
    pageNumber: 0,
    loadQuotes: true,
  });

  const [form] = Form.useForm();

  const { fetchedData: instrumentsOptions, loading } = useHttpGet<
    IResultListResponse<IInstrument>
  >(`${APIRoutes.INSTRUMENTS}`, {
    condition: !!visible,
    dependencies: [visible, instrumentsFilter],
    query: instrumentsFilter,
    defaultValue: {} as IResultListResponse<IInstrument>,
    resolve: (response: IResultListResponse<IInstrument>) => {

      // run only once on start
      if (instrumentIdsWithQuotesOn.length === 0) {
        const ids = response?.result?.list?.map((el: IInstrument) => el.id);
        setInstrumentIdsWithQuotesOn(ids);
      }
    },
    reject: CommonService.showAllErrors,
  });

  useEffect(() => {
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${process.env.REACT_APP_ENGINE}/hubs/quotes`, {
        headers: {
          "Access-Control-Allow-Origin": "*",
        },
      })
      .configureLogging(signalR.LogLevel.Information)
      .build();

    setHubConnection(connection);

  }, []);

  useEffect(() => {
    start();

    hubConnection?.onclose(async () => {
      await start();
    });

    hubConnection?.on("log", (response: any) => {
      setLogs((array) => [response, ...array]);
    });
  }, [hubConnection]);

  const start = async () => {
    try {
      if (hubConnection) {
        await hubConnection?.start();
        setLogs((array) => [{ date: new Date(), logLevel: LogLevelsTypes.Information, message: "SignalR Connected" }, ...array]);
      }
    } catch (err: any) {
      console.log(err);

      setLogs((array) => [{ date: new Date(), logLevel: LogLevelsTypes.Error, message: err?.message ?? err }, ...array]);
      setTimeout(start, 5000);
    }
  };

  const ping = async () => {
    try {
      if (hubConnection.state === "Connected") {
        await hubConnection.invoke("Ping");
      }
    } catch (err) {
      console.log(err);
      setLogs((array) => [{ date: new Date(), logLevel: LogLevelsTypes.Error, message: err }, ...array]);
    }
  };

  const parseNews = async () => {
    try {
      if (hubConnection.state === "Connected")
        await hubConnection.invoke("GetNews");
    } catch (err) {
      console.log(err);
      setLogs((array) => [{ date: new Date(), logLevel: LogLevelsTypes.Error, message: err }, ...array]);
    }
  };

  const updateSortForCryptos = async () => {
    try {
      if (hubConnection.state === "Connected")
        await hubConnection.invoke("UpdateSortForCryptos");
    } catch (err) {
      console.log(err);
      setLogs((array) => [{ date: new Date(), logLevel: LogLevelsTypes.Error, message: err }, ...array]);
    }
  };

  const resetSortForCryptos = async () => {
    try {
      if (hubConnection.state === "Connected")
        await hubConnection.invoke("ResetSortForCryptos");
    } catch (err) {
      console.log(err);
      setLogs((array) => [{ date: new Date(), logLevel: LogLevelsTypes.Error, message: err }, ...array]);
    }
  };

  const parsePrices = async ({
    instrumentIds,
    period,
  }: {
    instrumentIds: number[];
    period: any[];
  }) => {
    try {
      await hubConnection.invoke(
        "ParsePrices",
        new Date(period[0]).toISOString(),
        new Date(period[1]).toISOString(),
        instrumentIds
      );
    } catch (err) {
      console.log(err);
      setLogs((array) => [{ date: new Date(), logLevel: LogLevelsTypes.Error, message: err }, ...array]);
    }
  };

  const parseMissingPrices = async ({
    instrumentIds,
  }: {
    instrumentIds: number[];
  }) => {
    try {
      await hubConnection.invoke("ParseMissingPrices", instrumentIds);
    } catch (err) {
      console.log(err);
      setLogs((array) => [{ date: new Date(), logLevel: LogLevelsTypes.Error, message: err }, ...array]);
    }
  };


  const parseMissingGlobalHistory = async () => {
    try {
      await hubConnection.invoke("GetMissingGlobalHistory");
    } catch (err) {
      console.log(err);
      setLogs((array) => [{ date: new Date(), logLevel: LogLevelsTypes.Error, message: err }, ...array]);
    }
  };

  const generateInstrumentVolatility = async ({
    instrumentIds,
    period,
  }: {
    instrumentIds: number[];
    period: any[];
  }) => {
    try {
      await hubConnection.invoke(
        "GenerateInstrumentVolatilityHistory",
        new Date(period[0]).toISOString(),
        new Date(period[1]).toISOString(),
        instrumentIds
      );
    } catch (err) {
      console.log(err);
      setLogs((array) => [{ date: new Date(), logLevel: LogLevelsTypes.Error, message: err }, ...array]);
    }
  };

  const generateCI = async ({
    ciType,
    period,
  }: {
    ciType: CITypes;
    period: any[];
  }) => {
    try {
      await hubConnection.invoke(
        "GenerateHistoricalCIValues",
        new Date(period[0]).toISOString(),
        new Date(period[1]).toISOString(),
        ciType
      );
    } catch (err) {
      console.log(err);
      setLogs((array) => [{ date: new Date(), logLevel: LogLevelsTypes.Error, message: err }, ...array]);
    }
  };

  const generateCIVolotility = async ({
    ciType,
    period,
  }: {
    ciType: CITypes;
    period: any[];
  }) => {
    try {
      await hubConnection.invoke(
        "GenerateCIVolatilityHistory",
        new Date(period[0]).toISOString(),
        new Date(period[1]).toISOString(),
        ciType
      );
    } catch (err) {
      console.log(err);
      setLogs((array) => [{ date: new Date(), logLevel: LogLevelsTypes.Error, message: err }, ...array]);
    }
  };

  const onSubmitForm = async (values: any) => {
    switch (modalMode) {
      case EngineModalMode.Prices: {
        await parsePrices(values);
        break;
      }
      case EngineModalMode.CI: {
        await generateCI(values);
        break;
      }
      case EngineModalMode.CIVolotility: {
        await generateCIVolotility(values);
        break;
      }
      case EngineModalMode.InstrumentVolotility: {
        await generateInstrumentVolatility(values);
        break;
      }
      case EngineModalMode.MissingPrices: {
        await parseMissingPrices(values);
        break;
      }

      default:
        break;
    }
  };

  const getTitle = () => {
    switch (modalMode) {
      case EngineModalMode.Prices:
        return "Parse Instruments Prices";
      case EngineModalMode.CI:
        return "Generate CI";
      case EngineModalMode.CIVolotility:
        return "Generate CI Volotility History";
      case EngineModalMode.InstrumentVolotility:
        return "Generate Instruments Volotility History";
      case EngineModalMode.MissingPrices:
        return "Parse Missing Prices";
    }
  };

  const setEngineModalMode = (mode: EngineModalMode) => {
    setIsModalVisible(true);

    setModalMode(mode);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    const newFilter = { ...instrumentsFilter };
    newFilter.pageSize = 50;
    setInstrumentsFilter(newFilter);
  };

  const handleInstrumentSearch = (value: string) => {
    const newFilter = { ...instrumentsFilter };
    newFilter.search = value;
    setInstrumentsFilter(newFilter);
  };

  const columns = [
    {
      title: "Log Level",
      dataIndex: "logLevel",
      key: "logLevel",
      render: (text: number) => LogLevelsTypes[text],
    },
    {
      title: "Message",
      dataIndex: "message",
      key: "message",
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (text: string) => moment(text).format("MM/DD/YYYY HH:mm"),
    },
  ];

  return (
    <>
      <Button onClick={start} type="default" disabled={hubConnection?.state === "Connected"}>
        Connect
      </Button>
      <Button disabled={hubConnection?.state === "Disconnected"} onClick={ping}>
        Ping
      </Button>
      <Button
        disabled={hubConnection?.state === "Disconnected"}
        onClick={parseNews}
      >
        Parse News
      </Button>

      <Button
        disabled={hubConnection?.state === "Disconnected"}
        onClick={() => {
          form.setFieldsValue({ presets: 50 });
          setEngineModalMode(EngineModalMode.Prices);
        }}
      >
        Parse prices
      </Button>

      <Button
        disabled={hubConnection?.state === "Disconnected"}
        onClick={() => {
          form.setFieldsValue({ presets: 50 });
          setEngineModalMode(EngineModalMode.MissingPrices);
        }}
      >
        Parse Missing Prices
      </Button>

      <Button
        disabled={hubConnection?.state === "Disconnected"}
        onClick={async () => {
          form.setFieldsValue({ presets: 50 });
          await parseMissingGlobalHistory();
        }}
      >
        Parse Missing Globad Market Data
      </Button>

      <Button
        disabled={hubConnection?.state === "Disconnected"}
        onClick={() => {
          setEngineModalMode(EngineModalMode.CI);
        }}
      >
        Generate CI
      </Button>

      <Button
        disabled={hubConnection?.state === "Disconnected"}
        onClick={() => {
          setEngineModalMode(EngineModalMode.CIVolotility);
        }}
      >
        Generate CI Volotility History
      </Button>

      <Button
        disabled={hubConnection?.state === "Disconnected"}
        onClick={() => {
          form.setFieldsValue({ presets: 50 });
          setEngineModalMode(EngineModalMode.InstrumentVolotility);
        }}
      >
        Generate Instruments Volotility History
      </Button>

      <Popconfirm
        title="Are you sure you want to update sort for cryptos instruments"
        onConfirm={updateSortForCryptos}
        okText="Yes"
        cancelText="No"
      >
        <Button disabled={hubConnection?.state === "Disconnected"}>
          Update Sort For Cryptos Instruments
        </Button>
      </Popconfirm>

      <Popconfirm
        title="Are you sure you want to reset sort for cryptos"
        onConfirm={resetSortForCryptos}
        okText="Yes"
        cancelText="No"
      >
        <Button disabled={hubConnection?.state === "Disconnected"}>
          Reset Sort For Cryptos
        </Button>
      </Popconfirm>

      <Button onClick={() => setLogs([])}>Clear logs</Button>

      <Table
        rowKey={(record) => `${record?.date} ${Math.random()}`}
        columns={columns}
        dataSource={logs}
        pagination={false}
        className="mt-2"
      />

      <Modal
        title={getTitle()}
        visible={visible}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
        ]}
        onCancel={handleCancel}
        width={1000}
      >
        <Form
          onFinish={onSubmitForm}
          {...formItemLayout}
          initialValues={{
            presets: instrumentsFilter.pageSize,
          }}
          form={form}
        >
          {modalMode !== EngineModalMode.MissingPrices && (
            <Form.Item
              label="Period"
              name="period"
              rules={[
                { required: true, message: "Please select period of time!" },
              ]}
            >
              <RangePicker />
            </Form.Item>
          )}

          {(modalMode === EngineModalMode.Prices ||
            modalMode === EngineModalMode.InstrumentVolotility ||
            modalMode === EngineModalMode.MissingPrices) && (
              <>
                <Form.Item
                  label="Select all with quotes ON"
                  name="_all"
                >
                  <Switch onClick={(checked) => {
                    if (checked) {
                      form.setFieldsValue({ instrumentIds: instrumentIdsWithQuotesOn })
                    }
                  }} />
                </Form.Item>

                <Form.Item
                  label="Instruments"
                  name="instrumentIds"
                  rules={[
                    { required: true, message: "Please select instrument(s)!" },
                  ]}
                >
                  <Select
                    mode="tags"
                    allowClear={true}
                    showSearch={true}
                    style={{ width: "100%" }}
                    placeholder="Instruments"
                    loading={loading}
                    onSearch={handleInstrumentSearch}
                  >
                    {instrumentsOptions.result?.list?.map((el: IInstrument) => (
                      <Select.Option key={el.id} value={`${el.id}`}>
                        {el.name} [{el.id}]
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </>
            )}

          {(modalMode === EngineModalMode.CI ||
            modalMode === EngineModalMode.CIVolotility) && (
              <Form.Item label="Presets" name="ciType">
                <Select
                  style={{ width: "100%" }}
                  placeholder=""
                  loading={loading}
                >
                  <Select.Option key={CITypes.CI10} value={CITypes.CI10}>
                    CI 10
                  </Select.Option>
                  <Select.Option key={CITypes.CI20} value={CITypes.CI20}>
                    CI 20
                  </Select.Option>
                </Select>
              </Form.Item>
            )}

          <Form.Item wrapperCol={{ offset: 5, span: 19 }}>
            <Button htmlType="submit">{getTitle()}</Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default Engine;
